import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { MapPin, X, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import MapOverlay from '../MapOverlay';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Restaurant } from '../Manager/restaurant.types';
import { performSmartSearch } from '../../lib/api/restaurant.apis';
import { MdArrowBackIosNew } from 'react-icons/md';
import { slugify, useUserActivity } from './Explore/services';
import CustomCheckbox from './Components/CheckBox';
import FilterDropdown from './Components/FilterDropdown';
import { getBoostScore } from './Components/getBoostScore';
import RestCard from './Components/RestCard';

/* ── tiny helpers ─────────────────────────────────────────────────────────── */
const chipCls = (active: boolean) =>
  `px-4 py-2 rounded-full text-sm font-medium border transition-all duration-150 cursor-pointer select-none ${
    active
      ? 'bg-orange-600 border-orange-600 text-white shadow-sm'
      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
  }`;

const SECTIONS = [
  { id: 'establishmentType', label: 'Establishment' },
  { id: 'experiences',       label: 'Experience'    },
  { id: 'mealType',          label: 'Meal type'     },
  { id: 'cuisines',          label: 'Cuisines'      },
] as const;

type FilterKey = 'establishmentType' | 'mealType' | 'cuisines' | 'experiences';

export default function SearchResultsPage() {
  const [showMap,           setShowMap]           = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [loading,           setLoading]           = useState(false);
  const [loadingMore,       setLoadingMore]       = useState(false);
  const [restaurants,       setRestaurants]       = useState<Restaurant[]>([]);
  const [totalCount,        setTotalCount]        = useState(0);
  const [currentPage,       setCurrentPage]       = useState(1);
  const [hasMore,           setHasMore]           = useState(true);
  const [message,          setMessage]           = useState('');
  const observerTarget = useRef<HTMLDivElement>(null);

  const blankFilters = {
    establishmentType: [] as string[],
    mealType:          [] as string[],
    cuisines:          [] as string[],
    experiences:       [] as string[],
    priceRange:        [] as string[],
    rating:  'Any',
    openNow: false,
  };

  const [tempFilters,    setTempFilters]    = useState({ ...blankFilters, establishmentType: ['Restaurants'] });
  const [appliedFilters, setAppliedFilters] = useState({ ...blankFilters, establishmentType: ['Restaurants'] });
  const [collapsed, setCollapsed] = useState<Record<FilterKey, boolean>>({
    establishmentType: false,
    mealType:          false,
    cuisines:          false,
    experiences:       false,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { trackClick } = useUserActivity();
  const navigate = useNavigate();

  const q       = searchParams.get('q')       || '';
  const sortBy  = searchParams.get('sortBy')  || 'relevance';
  const filters = searchParams.get('filters')?.split(',') || [];

  const activeFilterCount =
    appliedFilters.establishmentType.length +
    appliedFilters.mealType.length +
    appliedFilters.cuisines.length +
    appliedFilters.experiences.length;

  const activePills = [
    ...appliedFilters.establishmentType,
    ...appliedFilters.mealType,
    ...appliedFilters.cuisines,
    ...appliedFilters.experiences,
  ];

  /* ── reset on new search ─────────────────────────────────────────────── */
  useEffect(() => {
    setRestaurants([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [q, filters.join(','), sortBy]);

  /* ── fetch ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    async function fetchResults() {
      currentPage === 1 ? setLoading(true) : setLoadingMore(true);
      try {
        const res = await performSmartSearch({ q, filters, page: currentPage, limit: 10, sortBy });
        const next = res.restaurants || [];
        setRestaurants(prev => currentPage === 1 ? next : [...prev, ...next]);
        if(res?.message) {
          setMessage(res?.message)
        } else {
          setMessage('')
        };
        setTotalCount(res.total || 0);
        setHasMore(next.length === 10);
      } catch {
        if (currentPage === 1) { setRestaurants([]); setTotalCount(0); }
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    }
    fetchResults();
  }, [q, filters.join(','), currentPage, sortBy]);

  /* ── infinite scroll ─────────────────────────────────────────────────── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && hasMore && !loading && !loadingMore) setCurrentPage(p => p + 1); },
      { threshold: 0.1 }
    );
    const t = observerTarget.current;
    if (t) observer.observe(t);
    return () => { if (t) observer.unobserve(t); };
  }, [hasMore, loading, loadingMore]);

  /* ── static data ─────────────────────────────────────────────────────── */
  const OPTIONS: Record<FilterKey, string[]> = {
    establishmentType: ['Restaurants', 'Bars & Pubs', 'Coffee & Tea', 'Bakeries'],
    mealType:          ['Breakfast', 'Brunch', 'Lunch', 'Dinner'],
    cuisines:          ['African', 'Indian', 'Italian', 'Chinese', 'Mexican', 'Japanese', 'Thai', 'American'],
    experiences:       ['Dining', 'Family Friendly', 'Romantic', 'Business', 'Outdoor', 'Date'],
  };

  /* ── helpers ─────────────────────────────────────────────────────────── */
  const boostedRestaurants = useMemo(
    () => [...restaurants].sort((a, b) => getBoostScore(b, appliedFilters) - getBoostScore(a, appliedFilters)),
    [restaurants, appliedFilters]
  );

  const toggleTemp = (key: FilterKey, value: string) =>
    setTempFilters(prev => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });

  const applyFilters = () => {
    setAppliedFilters(tempFilters);
    const urlFilters = [
      ...tempFilters.establishmentType,
      ...tempFilters.mealType,
      ...tempFilters.cuisines,
      ...tempFilters.experiences,
      tempFilters.rating !== 'Any' ? `rating:${tempFilters.rating}` : null,
      tempFilters.priceRange.length   ? `price:${tempFilters.priceRange[0]}`   : null,
    ].filter(Boolean);
    setSearchParams({ q, filters: urlFilters.join(','), sortBy });
    setShowMobileFilters(false);
  };

  const clearFilters = () => {
    setTempFilters(blankFilters);
    setAppliedFilters(blankFilters);
    setSearchParams({ q, filters: '', sortBy });
  };

  const removePill = (value: string) => {
    const next = { ...appliedFilters };
    (Object.keys(next) as (keyof typeof next)[]).forEach(k => {
      if (Array.isArray(next[k])) (next[k] as string[]) = (next[k] as string[]).filter(v => v !== value);
    });
    setAppliedFilters(next);
    setTempFilters(next);
  };

  const handleCardClick = useCallback((id: string, name: string) => {
    trackClick(id);
    navigate(`/restaurant/${id}/${slugify(name)}`);
  }, [trackClick, navigate]);

  /* ── sub-components ──────────────────────────────────────────────────── */
  const Spinner = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
    <div className={`rounded-full border-2 border-gray-200 border-t-orange-600 animate-spin ${
      size === 'sm' ? 'w-5 h-5' : 'w-9 h-9'
    }`} />
  );

  /** Sidebar collapsible section */
  const SidebarSection = ({ id, label }: { id: FilterKey; label: string }) => (
    <div className="py-4 border-t border-gray-100">
      <button
        className="flex items-center justify-between w-full mb-3 group"
        onClick={() => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }))}
      >
        <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">
          {label}
        </span>
        {collapsed[id]
          ? <ChevronDown size={13} className="text-gray-400" />
          : <ChevronUp   size={13} className="text-gray-400" />}
      </button>
      {!collapsed[id] && (
        <div className="flex flex-col gap-2">
          {OPTIONS[id].map(item => (
            <CustomCheckbox
              key={item}
              checked={(tempFilters[id] as string[]).includes(item)}
              onChange={() => toggleTemp(id, item)}
              label={item}
            />
          ))}
        </div>
      )}
    </div>
  );

  /* ── render ──────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-stone-50">

      {/* ── Topbar ── */}
      <div className="sticky top-[68px] z-40 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-14 flex items-center justify-between gap-4">

          {/* Left: back + query */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/')}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
            >
              <MdArrowBackIosNew size={12} className="text-gray-700" />
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm text-gray-500 truncate">
                {loading && currentPage === 1
                  ? 'Searching…'
                  : (
                    message       ? message
                    : totalCount > 0
                      ? <>Results for <span className="font-semibold text-gray-900">"{q}"</span></>
                      : `No places found for "${q}"`
                  )}
              </p>
              {!loading && (
                <span className="flex-shrink-0 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-0.5">
                  {totalCount.toLocaleString()} places
                </span>
              )}
            </div>
          </div>

          {/* Right: map btn (desktop) */}
          <button
            onClick={() => setShowMap(!showMap)}
            className="hidden lg:flex flex-shrink-0 items-center gap-1.5 px-4 h-8 bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold rounded-full transition-all duration-150 tracking-wide"
          >
            <MapPin size={13} />
            Map view
          </button>
        </div>

        {/* Mobile filter + map row */}
        <div className="lg:hidden flex items-center justify-between px-4 py-2.5 border-t border-gray-100">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-gray-400 transition-all duration-150"
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className="w-4 h-4 flex items-center justify-center bg-orange-600 text-white text-[10px] font-bold rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowMap(!showMap)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-700 transition-all duration-150"
          >
            <MapPin size={13} />
            Map
          </button>
        </div>
      </div>

      {/* ── Page body ── */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 flex gap-6 items-start">

        {/* ── Sidebar ── */}
        <aside className="hidden lg:block w-60 flex-shrink-0 sticky top-36">
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">

            {/* Sidebar header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-base font-semibold text-gray-900">Filters</span>
              <button
                onClick={clearFilters}
                className="text-[11px] font-semibold uppercase tracking-wider text-orange-600 hover:text-orange-700 hover:bg-orange-50 px-2 py-1 rounded-md transition-all duration-150"
              >
                Clear all
              </button>
            </div>

            <div className="px-5">
              {/* Price */}
              <div className="py-4">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">Price</p>
                <FilterDropdown
                  label="Price"
                  options={['Any', 'Affordable', 'Mid-range', 'Premium', 'Fine Dining']}
                  value={tempFilters.priceRange[0] || 'Any'}
                  onChange={val => setTempFilters(prev => ({ ...prev, priceRange: val === 'Any' ? [] : [val] }))}
                />
              </div>

              {/* Rating */}
              <div className="py-4 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">Rating</p>
                <FilterDropdown
                  label="Rating"
                  options={['Any', '4+', '3+']}
                  value={tempFilters.rating || 'Any'}
                  onChange={val => setTempFilters(prev => ({ ...prev, rating: val === 'Any' ? '' : val }))}
                />
              </div>

              {SECTIONS.map(s => <SidebarSection key={s.id} id={s.id} label={s.label} />)}
            </div>

            <div className="px-5 pb-5 pt-2">
              <button
                onClick={applyFilters}
                className="w-full py-3 bg-orange-600 hover:bg-orange-700 active:scale-[.98] text-white text-sm font-semibold rounded-xl transition-all duration-150 shadow-sm shadow-orange-200 tracking-wide"
              >
                Show results
              </button>
            </div>
          </div>
        </aside>

        {/* ── Results ── */}
        <div className="flex-1 min-w-0">

          {/* Active filter pills */}
          {activePills.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {activePills.map(pill => (
                <span
                  key={pill}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded-full text-xs font-medium"
                >
                  {pill}
                  <button
                    onClick={() => removePill(pill)}
                    className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-orange-200 transition-colors"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-xs text-gray-400 hover:text-gray-700 px-2 py-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Loading — initial */}
          {loading && currentPage === 1 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-24">
              <Spinner />
              <p className="text-sm text-gray-400">Finding great places…</p>
            </div>

          ) : boostedRestaurants.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
              <span className="text-5xl">🍽️</span>
              <h3 className="text-xl font-semibold text-gray-800">No results found</h3>
              <p className="text-sm text-gray-400 max-w-xs">Try adjusting your filters or searching for something else.</p>
            </div>

          ) : (
            <>
              <div className="flex flex-col gap-3">
                {boostedRestaurants.map(restaurant => (
                  <RestCard
                    key={restaurant._id}
                    size={40}
                    restaurant={restaurant}
                    onCardClick={handleCardClick}
                  />
                ))}
              </div>

              {/* Loading more */}
              {loadingMore && (
                <div className="flex items-center justify-center gap-3 py-8 text-sm text-gray-400">
                  <Spinner size="sm" />
                  <span>Loading more…</span>
                </div>
              )}

              {/* Scroll sentinel */}
              <div ref={observerTarget} className="h-1" />

              {/* End of results */}
              {!hasMore && restaurants.length > 0 && (
                <div className="flex items-center gap-4 py-8 text-xs text-gray-400">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span>All {totalCount.toLocaleString()} results shown</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Mobile filter sheet ── */}
      {showMobileFilters && (
        <div
          className="fixed inset-0 bg-black/40 z-50 lg:hidden"
          onClick={() => setShowMobileFilters(false)}
        >
          <div
            className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl max-h-[88vh] flex flex-col shadow-2xl"
            onClick={e => e.stopPropagation()}
            style={{ animation: 'slideUp .28s cubic-bezier(.4,0,.2,1)' }}
          >
            {/* Handle */}
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mt-3 flex-shrink-0" />

            {/* Sheet header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Sheet body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">

              {/* Price */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">Price range</p>
                <div className="flex flex-wrap gap-2">
                  {['Affordable', 'Mid-range', 'Premium', 'Fine Dining'].map(p => (
                    <button
                      key={p}
                      className={chipCls(tempFilters.priceRange.includes(p))}
                      onClick={() => setTempFilters(prev => ({
                        ...prev,
                        priceRange: prev.priceRange.includes(p) ? [] : [p],
                      }))}
                    >{p}</button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">Rating</p>
                <div className="flex flex-wrap gap-2">
                  {['Any', '4+', '3+'].map(r => (
                    <button
                      key={r}
                      className={chipCls(tempFilters.rating === r)}
                      onClick={() => setTempFilters(prev => ({ ...prev, rating: r }))}
                    >{r === 'Any' ? 'Any rating' : `${r} stars`}</button>
                  ))}
                </div>
              </div>

              {/* Other filter sections */}
              {SECTIONS.map(({ id, label }) => (
                <div key={id}>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3">{label}</p>
                  <div className="flex flex-wrap gap-2">
                    {OPTIONS[id].map(item => (
                      <button
                        key={item}
                        className={chipCls((tempFilters[id] as string[]).includes(item))}
                        onClick={() => toggleTemp(id, item)}
                      >{item}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Sheet footer */}
            <div className="flex gap-3 px-5 py-4 border-t border-gray-100 bg-white flex-shrink-0">
              <button
                onClick={clearFilters}
                className="flex-1 py-3.5 border-2 border-gray-200 bg-white text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
              >
                Clear all
              </button>
              <button
                onClick={applyFilters}
                className="flex-1 py-3.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-xl transition-all duration-150 shadow-sm shadow-orange-200"
              >
                Show results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sheet slide-up keyframe */}
      <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>

      <MapOverlay show={showMap} onClose={() => setShowMap(false)} restaurants={restaurants} />
    </div>
  );
}